import type { ReactNode } from 'react'
import { cn } from '../cn'
import { UiLink } from '../link'

export interface StatTileProps {
  label: string
  value: ReactNode
  /** Supporting line ("12 holdings across 3 portfolios"). */
  note?: ReactNode
  /** Makes the whole tile a link (UI-004: each KPI links to its register). */
  href?: string
  icon?: ReactNode
  className?: string
}

/**
 * KPI tile. Group several in `<StatGroup>` for the prototype's joined strip, or use alone.
 */
export function StatTile({ label, value, note, href, icon, className }: StatTileProps) {
  const body = (
    <>
      <span className="flex items-center justify-between gap-3 text-[13px] font-semibold text-sage">
        {label}
        {icon ? (
          <span aria-hidden="true" className="text-teal-400 [&>svg]:size-[18px]">
            {icon}
          </span>
        ) : null}
      </span>
      <span className="mt-2 block font-display text-[32px] font-semibold leading-tight tracking-[-0.02em] text-teal tabular-nums">
        {value}
      </span>
      {note ? <span className="mt-1.5 block text-xs leading-relaxed text-sage">{note}</span> : null}
    </>
  )
  const classes = cn('group flex min-w-0 flex-col bg-surface px-6 py-5', className)
  if (href) {
    return (
      <UiLink
        href={href}
        className={cn(
          classes,
          'no-underline transition-colors hover:bg-[#f8faf6] focus-visible:z-10',
        )}
      >
        {body}
      </UiLink>
    )
  }
  return <div className={classes}>{body}</div>
}

/** Joined strip of stat tiles with shared borders (4-up on desktop, 2-up on phones). */
export function StatGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line lg:grid-cols-4',
        className,
      )}
    >
      {children}
    </div>
  )
}
